/**
 * router/routes.js
 *
 * Daftar semua route Portal IHC, dikelompokkan per area:
 *   - Authentication Routes (Public): login, register, forgot-password
 *   - Main Application (Protected):
 *       • Dashboard
 *       • Master Data: access, analysis-form, branch, medicine, intervention,
 *                      assessment, service-salary, bank
 *       • Pusat Dokumen: document-center
 *       • Penomoran Surat: personnel/letter-number (Kepegawaian › No Surat)
 *       • Settings:    reason, transportation, payroll-schedule,
 *                      attendance-schedule
 *
 * Konvensi:
 * - Semua route protected wajib pakai `meta.authRequired: true`.
 * - `meta.title` wajib diisi (dipakai untuk update document.title oleh
 *   beforeResolve di router/index.js).
 * - Component pakai dynamic import (`() => import(...)`) supaya code-splitting
 *   otomatis per route.
 *
 * Cara tambah route baru: ikuti pattern existing, kelompokkan di bawah
 * komentar section yang sesuai (MASTER DATA / SETTINGS / dst).
 */
export default [
  // ==============================
  // Authentication Routes (Public)
  // ==============================
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: {
      title: "Login",
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/account/register.vue"),
    meta: {
      title: "Register",
    },
  },
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: {
      title: "Forgot Password",
    },
  },

  // ==============================
  // Main Application Routes (Protected)
  // ==============================
  {
    path: "/",
    name: "default",
    meta: {
      title: "Dashboard",
      authRequired: true,
    },
    component: () => import("../views/dashboard/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                 MASTER DATA                                */
  /* -------------------------------------------------------------------------- */
  {
    path: "/master-data/access",
    name: "access",
    meta: {
      title: "Hak Akses",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/index.vue"),
  },
  {
    path: "/master-data/access/role/tambah",
    name: "role-tambah",
    meta: {
      title: "Tambah Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/access/role/edit/:id",
    name: "role-edit",
    meta: {
      title: "Edit Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/analysis-form",
    name: "analysis-form",
    meta: {
      title: "Form Analisa",
      authRequired: true,
    },
    component: () => import("../views/master-data/analysis-form/index.vue"),
  },
  {
    path: "/master-data/branch",
    name: "branch",
    meta: {
      title: "Cabang",
      authRequired: true,
    },
    component: () => import("../views/master-data/branch/index.vue"),
  },
  {
    path: '/master-data/medicine',
    name: 'medicine',
    meta: {
      title: 'Obat',
      authRequired: true,
    },
    component: () => import('../views/master-data/medicine/index.vue'),
  },
  {
    path: '/master-data/intervention',
    name: 'intervention',
    meta: {
      title: 'Intervensi',
      authRequired: true,
    },
    component: () => import('../views/master-data/intervention/index.vue'),
  },
  {
    path: '/master-data/assessment',
    name: 'assessment',
    meta: {
      title: 'Asesmen',
      authRequired: true,
    },
    component: () => import('../views/master-data/assessment/index.vue'),
  },
  {
    path: '/master-data/service-salary',
    name: 'service-salary',
    meta: {
      title: 'Gaji Layanan',
      authRequired: true,
    },
    component: () => import('../views/master-data/service-salary/index.vue'),
  },
  {
    path: "/master-data/bank",
    name: "bank",
    meta: {
      title: "Bank",
      authRequired: true,
    },
    component: () => import("../views/master-data/bank/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  SETTINGS                                  */
  /* -------------------------------------------------------------------------- */
  {
    path: "/settings/reason",
    name: "reason",
    meta: {
      title: "Kebijakan Alasan Stop",
      authRequired: true,
    },
    component: () => import("../views/settings/reason/index.vue"),
  },
  {
    path: "/settings/transportation",
    name: "transportation",
    meta: {
      title: "Kebijakan Transportasi",
      authRequired: true,
    },
    component: () => import("../views/settings/transportation/index.vue"),
  },
  {
    path: "/settings/payroll-schedule",
    name: "payroll-schedule",
    meta: {
      title: "Kebijakan Jadwal Penggajian",
      authRequired: true,
    },
    component: () => import("../views/settings/payroll-schedule/index.vue"),
  },
  {
    path: "/settings/attendance-schedule",
    name: "attendance-schedule",
    meta: {
      title: "Kebijakan Jadwal Absensi",
      authRequired: true,
    },
    component: () => import("../views/settings/attendance-schedule/index.vue"),
  },
  {
    path: "/settings/time-off",
    name: "time-off",
    meta: {
      title: "Kebijakan Kehadiran",
      authRequired: true,
    },
    component: () => import("../views/settings/time-off/index.vue"),
  },
  {
    path: "/settings/compensation",
    name: "compensation",
    meta: {
      title: "Kebijakan Kompensasi",
      authRequired: true,
    },
    component: () => import("../views/settings/compensation/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                 ATTENDANCE                                 */
  /*                                                                            */
  /* Modul Absensi. Fitur "Monitoring Absensi" memantau kehadiran SELURUH       */
  /* karyawan dalam satu halaman, dipisah per organisasi memakai tab pane       */
  /* lokal (Kantor / Visit / Perawat) — bukan nested route.                     */
  /* -------------------------------------------------------------------------- */
  {
    path: "/attendance/monitoring",
    name: "attendance-monitoring",
    meta: {
      title: "Monitoring Absensi",
      authRequired: true,
    },
    component: () => import("../views/attendance/monitoring/index.vue"),
  },
  {
    path: "/attendance/request-time-off",
    name: "attendance-request-time-off",
    meta: {
      title: "Perizinan",
      authRequired: true,
    },
    component: () =>
        import("../views/attendance/request-time-off/index.vue"),
  },
  /* -------------------------------------------------------------------------- */
  /* Fitur "Pengajuan Lupa Absensi" (submission) — memvalidasi pengajuan        */
  /* karyawan yang lupa clock in/out. Dipisah per organisasi memakai tab pane   */
  /* lokal (Kantor / Visit / Perawat). Saat ini baru organisasi Office (Kantor) */
  /* yang memiliki API; Visit & Perawat masih placeholder.                      */
  /* -------------------------------------------------------------------------- */
  {
    path: "/attendance/submission",
    name: "attendance-submission",
    meta: {
      title: "Pengajuan Lupa Absensi",
      authRequired: true,
    },
    component: () => import("../views/attendance/submission/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  PERSONNEL                                 */
  /* -------------------------------------------------------------------------- */
  {
    path: "/personnel/employee",
    name: "employee",
    meta: {
      title: "Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/index.vue"),
  },
  {
    path: "/personnel/employee/create",
    name: "employee-create",
    meta: {
      title: "Tambah Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/create/index.vue"),
  },
  {
    path: "/personnel/employee/onboarding",
    name: "employee-onboarding",
    meta: {
      title: "Pendaftaran Link",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/onboarding/index.vue"),
  },
  {
    path: "/personnel/credential",
    name: "credential",
    meta: {
      title: "Kredensial",
      authRequired: true,
    },
    component: () => import("../views/personnel/credential/index.vue"),
  },
  {
    path: "/personnel/career",
    name: "career",
    meta: {
      title: "Karir",
      authRequired: true,
    },
    component: () => import("../views/personnel/career/index.vue"),
  },
  {
    path: "/personnel/applicant",
    name: "applicant",
    meta: {
      title: "Pelamar",
      authRequired: true,
    },
    component: () => import("../views/personnel/applicant/index.vue"),
  },
  {
    path: "/personnel/job-position",
    name: "job-position",
    meta: {
      title: "Jabatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/job-position/index.vue"),
  },
  {
    path: "/personnel/division",
    name: "division",
    meta: {
      title: "Divisi",
      authRequired: true,
    },
    component: () => import("../views/personnel/division/index.vue"),
  },
  {
    path: "/personnel/warning-letter",
    name: "warning-letter",
    meta: {
      title: "Surat Peringatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/warning-letter/index.vue"),
  },
  /* Perizinan Perawat — daftar izin LINTAS karyawan.                        */
  /* Berbeda dari section "Perizinan" di Detail Karyawan Homecare, yang      */
  /* hanya menampilkan izin SATU orang lewat endpoint                        */
  /* /personnel/employee/{userId}/nurse-leave.                               */
  {
    path: "/personnel/nurse-leave",
    name: "nurse-leave",
    meta: {
      title: "Perizinan Perawat",
      authRequired: true,
    },
    component: () => import("../views/personnel/nurse-leave/index.vue")
  },
  {
    path: "/personnel/absence",
    name: "absence",
    meta: {
      title: "Mangkir",
      authRequired: true,
    },
    component: () => import("../views/personnel/absence/index.vue"),
  },
  {
    path: "/personnel/announcement",
    name: "announcement",
    meta: {
      title: "Pengumuman",
      authRequired: true,
    },
    component: () => import("../views/personnel/announcement/index.vue"),
  },
  {
    path: "/personnel/employee-separation",
    name: "employee-separation",
    meta: {
      title: "Pemberhentian Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee-separation/index.vue"),
  },
  /* ----------------------------------------------------------------------- */
  /*                       DETAIL KARYAWAN (per organisasi)                  */
  /*                                                                         */
  /* Detail Karyawan dipisah per organisasi: Officer / Homecare / Visitor.   */
  /* Tiap organisasi punya halaman shell sendiri (header + nav section) dan  */
  /* sekumpulan NESTED CHILD ROUTE per section (Profil, Absensi, dst).       */
  /*                                                                         */
  /* Pola nested route dipilih agar tiap section bisa di-deep-link & punya   */
  /* tombol back browser; shell (header + tab) tetap mount saat pindah       */
  /* section. Sub-tab ringan DI DALAM section (mis. Data Karyawan /          */
  /* Pendidikan / Pengalaman / Kontak Darurat) tetap tab-pane lokal.         */
  /*                                                                         */
  /* Saat ini baru Officer yang diimplementasi (statis). Homecare & Visitor  */
  /* menyusul dengan pola folder yang sama (detail/homecare, detail/visitor).*/
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/officer/:id",
    component: () => import("../views/personnel/employee/detail/officer/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    // redirect default ke section Profil
    redirect: { name: "employee-detail-officer-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-officer-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/ProfileSection.vue"
                ),
      },
      {
        path: "absensi",
        name: "employee-detail-officer-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/AttendanceSection.vue"
                ),
      },
      {
        path: "kontrak",
        name: "employee-detail-officer-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
                ),
      },
      {
        path: "payroll",
        name: "employee-detail-officer-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/PayrollSection.vue"
                ),
      },
      {
        path: "perizinan",
        name: "employee-detail-officer-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/PermissionSection.vue"
                ),
      },
      {
        path: "sp",
        name: "employee-detail-officer-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
                ),
      },
    ],
  },

  /* ----------------------------------------------------------------------- */
  /* Detail Karyawan — Visitor                                               */
  /*                                                                         */
  /* Pola folder & route sama dengan Officer (shell + child route per        */
  /* section). Saat ini baru section Profil yang diimplementasi; section     */
  /* lain pakai SectionPlaceholder (di-reuse dari folder officer).           */
  /*                                                                         */
  /* Profil Visitor punya sub-tab tambahan "Kredensial" (select2 multiple). */
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/visitor/:id",
    component: () => import("../views/personnel/employee/detail/visitor/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    redirect: { name: "employee-detail-visitor-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-visitor-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/visitor/sections/ProfileSection.vue"
                ),
      },
      {
        path: "absensi",
        name: "employee-detail-visitor-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/visitor/sections/AttendanceSection.vue"
                ),
      },
      {
        path: "kontrak",
        name: "employee-detail-visitor-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Kontrak Visitor identik dengan Officer → reuse section officer
        // (self-contained, hanya butuh prop employeeId dari router-view).
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
                ),
      },
      {
        path: "payroll",
        name: "employee-detail-visitor-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/visitor/sections/PayrollSection.vue"
                ),
      },
      {
        path: "perizinan",
        name: "employee-detail-visitor-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Perizinan Visitor identik dengan Officer → reuse section officer.
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/PermissionSection.vue"
                ),
      },
      {
        path: "sp",
        name: "employee-detail-visitor-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        // SP (Surat Peringatan) Visitor identik dengan Officer → reuse section officer.
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
                ),
      },
      {
        path: "komplain",
        name: "employee-detail-visitor-complaint",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/visitor/sections/ComplaintSection.vue"
                ),
      },
    ],
  },

  /* ----------------------------------------------------------------------- */
  /* Detail Karyawan — Homecare                                              */
  /*                                                                         */
  /* Pola folder & route sama dengan Officer/Visitor (shell + child route    */
  /* per section). Perbedaan khas Homecare:                                  */
  /*  - Profil punya sub-tab "Kredensial" (opsi master organization=homecare)*/
  /*  - Absensi memakai endpoint homecare-attendance (Absensi Perawat)       */
  /*  - Payroll sub-tab "Slip Gaji" memakai endpoint payroll/homecare/*      */
  /*  - Perizinan memakai endpoint nurse-leave (Perizinan Perawat)           */
  /*  - Komplain di-reuse penuh dari Visitor (ComplaintSection visitor).     */
  /*                                                                         */
  /* Kontrak & SP di-reuse penuh dari Officer (identik lintas-organisasi).   */
  /* ----------------------------------------------------------------------- */
  {
    path: "/personnel/employee/homecare/:id",
    component: () => import("../views/personnel/employee/detail/homecare/index.vue"),
    meta: {
      title: "Detail Karyawan",
      authRequired: true,
    },
    redirect: { name: "employee-detail-homecare-profile" },
    children: [
      {
        path: "profil",
        name: "employee-detail-homecare-profile",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/homecare/sections/ProfileSection.vue"
                ),
      },
      {
        path: "absensi",
        name: "employee-detail-homecare-attendance",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/homecare/sections/AttendanceSection.vue"
                ),
      },
      {
        path: "kontrak",
        name: "employee-detail-homecare-contract",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Kontrak Homecare identik dengan Officer → reuse section officer.
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/ContractSection.vue"
                ),
      },
      {
        path: "payroll",
        name: "employee-detail-homecare-payroll",
        meta: { title: "Detail Karyawan", authRequired: true },
        component: () =>
            import(
                "../views/personnel/employee/detail/homecare/sections/PayrollSection.vue"
                ),
      },
      {
        path: "perizinan",
        name: "employee-detail-homecare-permission",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Perizinan Perawat Homecare — endpoint nurse-leave (filter rentang
        // tanggal + kartu kuota statis). Berbeda dari Perizinan Officer.
        component: () =>
            import(
                "../views/personnel/employee/detail/homecare/sections/PermissionSection.vue"
                ),
      },
      {
        path: "sp",
        name: "employee-detail-homecare-warning",
        meta: { title: "Detail Karyawan", authRequired: true },
        // SP (Surat Peringatan) Homecare identik dengan Officer → reuse section officer.
        component: () =>
            import(
                "../views/personnel/employee/detail/officer/sections/WarningSection.vue"
                ),
      },
      {
        path: "komplain",
        name: "employee-detail-homecare-complaint",
        meta: { title: "Detail Karyawan", authRequired: true },
        // Komplain Homecare identik dengan Visitor → reuse section visitor
        // (self-contained: maps + komponen komplain ada di folder visitor;
        //  endpoint complaint berbasis user_id, lintas-organisasi).
        component: () =>
            import(
                "../views/personnel/employee/detail/visitor/sections/ComplaintSection.vue"
                ),
      },
    ],
  },
  /* -------------------------------------------------------------------------- */
  /*                          NO SURAT (PENOMORAN SURAT)                        */
  /* -------------------------------------------------------------------------- */
  {
    path: "/personnel/letter-number",
    name: "letter-number",
    meta: {
      title: "Penomoran Surat",
      authRequired: true,
    },
    component: () => import("../views/personnel/letter-number/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                            PELAYANAN (SERVICE)                             */
  /* -------------------------------------------------------------------------- */
  {
    // Pasien & Penanggung Jawab
    path: "/service/patient-customer",
    name: "service-patient-customer",
    meta: {
      title: "Pasien dan Penanggung Jawab",
      authRequired: true,
    },
    component: () =>
        import("../views/service/patient-customer/index.vue"),
  },
  {
    // Detail Pasien
    path: "/service/patient/:id",
    name: "service-patient-detail",
    meta: {
      title: "Detail Pasien",
      authRequired: true,
    },
    component: () =>
        import("../views/service/patient-customer/detail/index.vue"),
  },
  {
    // Detail Penanggung Jawab
    path: "/service/customer/:id",
    name: "service-customer-detail",
    meta: {
      title: "Detail Penanggung Jawab",
      authRequired: true,
    },
    component: () =>
        import("../views/service/patient-customer/customer-detail/index.vue"),
  },
  {
    // Invoice
    path: "/service/invoice",
    name: "invoice",
    meta: {
      title: "Invoice",
      authRequired: true,
    },
    component: () => import("../views/service/invoice/index.vue"),
  },
  {
    // Invoice — Buat Invoice (form tambah; statis, API menyusul)
    path: "/service/invoice/create",
    name: "invoice-create",
    meta: {
      title: "Buat Invoice",
      authRequired: true,
    },
    component: () => import("../views/service/invoice/create/index.vue"),
  },
  {
    // Invoice — Detail Invoice (statis; endpoint detail belum tersedia).
    // Path ':id' dideklarasikan SETELAH '/create' — Vue Router memang
    // memprioritaskan segmen statis, tapi urutan ini menegaskan intent.
    path: "/service/invoice/:id",
    name: "invoice-detail",
    meta: {
      title: "Detail Invoice",
      authRequired: true,
    },
    component: () => import("../views/service/invoice/detail/index.vue"),
  },
  {
    // Alat Medis — master alat medis (dulu panel "Alat Terpasang" di Master
    // Data › Form Analisa; panel itu sudah dihapus). API tetap
    // /master/analysis-form/medical-device. Dokumentasi:
    // documentation/service/medical-device.md
    path: "/service/medical-device",
    name: "service-medical-device",
    meta: {
      title: "Alat Medis",
      authRequired: true,
    },
    component: () => import("../views/service/medical-device/index.vue"),
  },
  {
    // Tindakan Medis — master tindakan medis. UI sama dengan Alat Medis.
    // ✅ API: /service/care/medical-procedures (tanpa data dummy).
    // Dokumentasi: documentation/service/medical-action.md
    path: "/service/medical-action",
    name: "service-medical-action",
    meta: {
      title: "Tindakan Medis",
      authRequired: true,
    },
    component: () => import("../views/service/medical-action/index.vue"),
  },
  {
    // Target Bulanan (Target Poin) — sejajar Invoice di modul Pelayanan.
    // CRUD sudah tersambung API; export masih menunggu backend.
    path: "/service/target-point",
    name: "service-target-point",
    meta: {
      title: "Target Bulanan",
      authRequired: true,
    },
    component: () => import("../views/service/target-point/index.vue"),
  },
  {
    // Progres Poin — pemantauan pencapaian poin CS/CR per bulan.
    // Read-only: targetnya di-input dari Target Bulanan, poinnya dihitung
    // backend dari transaksi Invoice.
    path: "/service/progress-points",
    name: "service-progress-points",
    meta: {
      title: "Progres Poin",
      authRequired: true,
    },
    component: () => import("../views/service/progress-points/index.vue"),
  },
  {
    // Perawatan — monitoring order perawatan.
    path: "/service/treatment",
    name: "service-treatment",
    meta: {
      title: "Perawatan",
      authRequired: true,
    },
    component: () => import("../views/service/treatment/index.vue"),
  },
  {
    // Detail Perawatan — Detail dari order perawatan.
    // (visit / nurse / equipment / support / pharmacy).
    path: "/service/treatment/:orderId",
    name: "service-treatment-detail",
    meta: {
      title: "Detail Perawatan",
      authRequired: true,
    },
    component: () => import("../views/service/treatment/detail/index.vue"),
  },
  {
    // Komplain — daftar komplain pelanggan/pasien terhadap layanan
    path: "/service/complaint",
    name: "service-complaint",
    meta: {
      title: "Komplain",
      authRequired: true,
    },
    component: () => import("../views/service/complaint/index.vue"),
  },
  {
    // Detail Komplain — informasi, lampiran bukti, dan linimasa progres.
    path: "/service/complaint/:id",
    name: "service-complaint-detail",
    meta: {
      title: "Detail Komplain",
      authRequired: true,
    },
    component: () => import("../views/service/complaint/detail/index.vue"),
  },
  {
    // Homecare Record (HCR) — rekam perawatan homecare per pasien.
    // Daftar dengan dua tampilan (tabel / kartu) + filter panel.
    // Daftar, Detail, catatan HCR v1/v2, Keluhan & Target v3 & Resume HCR
    // memakai API /service/homecare-record (termasuk Unduh Jadwal Terapi);
    // Jadwal HCR & Foto v3 masih DUMMY (MOCK_FEATURES di
    // services/service/hcr.service.js).
    path: "/service/hcr",
    name: "service-hcr",
    meta: {
      title: "Homecare Record",
      authRequired: true,
    },
    component: () => import("../views/service/hcr/index.vue"),
  },
  {
    // Detail HCR — header pasien, treatment terakhir & tanda vital, jadwal
    // berikutnya, tab lokal Layanan | Resume HCR (?tab=resume), panel
    // Detail Pasien, dan modal Unduh HCR.
    path: "/service/hcr/:id",
    name: "service-hcr-detail",
    meta: {
      title: "Detail Homecare Record",
      authRequired: true,
    },
    component: () => import("../views/service/hcr/detail/index.vue"),
  },
  {
    // Buat Resume HCR — halaman form 6 bagian (Informasi Dasar, Keluhan &
    // Terapi, Obat, Jenis Luka, Catatan, Tanda Vital). Selesai simpan →
    // kembali ke Detail HCR tab Resume dan baris barunya disorot.
    path: "/service/hcr/:id/resume/create",
    name: "service-hcr-resume-create",
    meta: {
      title: "Buat Resume HCR",
      authRequired: true,
    },
    component: () => import("../views/service/hcr/resume/index.vue"),
  },
  {
    // Edit Resume HCR — halaman yang sama dengan Buat (mode dari nama route).
    path: "/service/hcr/:id/resume/:resumeId/edit",
    name: "service-hcr-resume-edit",
    meta: {
      title: "Edit Resume HCR",
      authRequired: true,
    },
    component: () => import("../views/service/hcr/resume/index.vue"),
  },
  {
    // Reservasi Website — reservasi masuk dari formulir website.
    path: "/service/reservation",
    name: "service-reservation",
    meta: {
      title: "Reservasi Website",
      authRequired: true,
    },
    component: () => import("../views/service/reservation/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  ETALASE                                   */
  /*                                                                            */
  /* Modul Etalase. Fitur "Etalase Visit" (nama file/kode: `visit`) berisi 3    */
  /* tab lokal (bukan nested route): Jadwal Harian (`daily-schedule` — SUDAH    */
  /* dikerjakan penuh, lihat views/etalase/visit/daily-schedule/), Daftar      */
  /* Pelanggan (`customer-list`) & Daftar Kunjungan (`visit-list`) — keduanya  */
  /* masih placeholder "Segera Hadir". SELURUH data fitur ini MASIH DUMMY      */
  /* (lihat services/etalase/visit.service.js).                                */
  /* -------------------------------------------------------------------------- */
  {
    path: "/etalase/visit",
    name: "etalase-visit",
    meta: {
      title: "Etalase Visit",
      authRequired: true,
    },
    component: () => import("../views/etalase/visit/index.vue"),
  },
  {
    path: "/etalase/nurse",
    name: "etalase-nurse",
    meta: {
      title: "Etalase Perawat",
      authRequired: true,
    },
    component: () => import("../views/etalase/nurse/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                        LAYANAN (SERVICE MANAGEMENT)                        */
  /* -------------------------------------------------------------------------- */
  {
    // Layanan — grup layanan, layanan, data periode, paket layanan &
    // layanan tambahan. Section ini SEJAJAR dengan Pelayanan (Service).
    // Seluruh tab sudah terhubung API (ringkasan, layanan, paket, tambahan).
    path: "/service-management/service",
    name: "service-management-service",
    meta: {
      title: "Layanan",
      authRequired: true,
    },
    component: () => import("../views/service-management/service/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                   MITRA (PARTNER)                          */
  /* -------------------------------------------------------------------------- */
  {
    path: "/partner/list",
    name: "partner-list",
    meta: {
      title: "Daftar Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/list/index.vue"),
  },
  {
    path: "/partner/list/:id",
    name: "partner-detail",
    meta: {
      title: "Detail Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/list/detail/index.vue"),
  },
  {
    path: "/partner/invoices",
    name: "partner-invoices",
    meta: {
      title: "Tagihan Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/invoices/index.vue"),
  },
  {
    path: "/partner/invoices/:id",
    name: "partner-invoice-detail",
    meta: {
      title: "Detail Tagihan Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/invoices/detail/index.vue"),
  },
  {
    path: "/partner/orders",
    name: "partner-orders",
    meta: {
      title: "Pesanan Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/orders/index.vue"),
  },
  {
    path: "/partner/orders/:id",
    name: "partner-order-detail",
    meta: {
      title: "Detail Pesanan Mitra",
      authRequired: true,
    },
    component: () => import("../views/partner/orders/detail/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                              PENGGAJIAN (PAYROLL)                          */
  /*                                                                            */
  /* Satu daftar penggajian dengan 3 tab organisasi: Kantor / Visitor / Perawat.*/
  /* Tab = segmen route `:category` (office | visitor | homecare) supaya tiap   */
  /* tab bisa di-deep-link & tombol back browser bekerja; komponen shell tetap  */
  /* sama sehingga pindah tab tidak me-mount ulang halaman (pane di-KeepAlive). */
  /*                                                                            */
  /* Regex `(office|visitor|homecare)` membuat `/payroll/create` TIDAK pernah   */
  /* tertangkap sebagai kategori. SELURUH data masih DUMMY — lihat              */
  /* services/payroll/payroll.service.js (flag USE_MOCK).                       */
  /* -------------------------------------------------------------------------- */
  {
    path: "/payroll",
    redirect: { name: "payroll", params: { category: "office" } },
  },
  {
    // Buat Penggajian — form langkah 1 (pilih kategori & periode).
    // Query opsional: ?category=office (prefill dari tab aktif).
    // Batch yang sudah ada dilanjutkan di /payroll/process/:category/:id.
    path: "/payroll/create",
    name: "payroll-create",
    meta: {
      title: "Buat Penggajian",
      authRequired: true,
    },
    component: () => import("../views/payroll/create/index.vue"),
  },
  {
    // Wizard proses penggajian (batch yang sudah dibuat / dilanjutkan).
    // Shell memuat header batch + stepper; tiap langkah = child route
    // supaya bisa di-refresh / di-deep-link tanpa kehilangan posisi.
    path: "/payroll/process/:category(office|visitor|homecare)/:id",
    component: () => import("../views/payroll/process/index.vue"),
    meta: { title: "Buat Penggajian", authRequired: true },
    redirect: { name: "payroll-process-validation" },
    children: [
      {
        path: "validasi",
        name: "payroll-process-validation",
        meta: { title: "Buat Penggajian", authRequired: true, step: 0 },
        component: () => import("../views/payroll/process/steps/ValidationStep.vue"),
      },
      {
        path: "verifikasi",
        name: "payroll-process-verification",
        meta: { title: "Buat Penggajian", authRequired: true, step: 1 },
        component: () => import("../views/payroll/process/steps/VerificationStep.vue"),
      },
      {
        path: "finalisasi",
        name: "payroll-process-finalization",
        meta: { title: "Buat Penggajian", authRequired: true, step: 2 },
        component: () => import("../views/payroll/process/steps/FinalizationStep.vue"),
      },
    ],
  },
  {
    // Detail Penggajian satu karyawan di dalam batch yang sedang diproses
    // (absensi, perizinan, komponen gaji, tahan gaji, validasi).
    path: "/payroll/process/:category(office|visitor|homecare)/:id/karyawan/:employeeId",
    name: "payroll-process-employee",
    meta: { title: "Detail Penggajian Karyawan", authRequired: true },
    component: () => import("../views/payroll/process/employee/index.vue"),
  },
  {
    // Daftar Penggajian per kategori (tab).
    path: "/payroll/:category(office|visitor|homecare)",
    name: "payroll",
    meta: {
      title: "Penggajian",
      authRequired: true,
    },
    component: () => import("../views/payroll/index.vue"),
  },
  {
    // Detail Penggajian — `id` = payroll_id.
    path: "/payroll/:category(office|visitor|homecare)/:id",
    name: "payroll-detail",
    meta: {
      title: "Detail Penggajian",
      authRequired: true,
    },
    component: () => import("../views/payroll/detail/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                        WEBSITE CONTENT (Kelola Website)                    */
  /* -------------------------------------------------------------------------- */
  {
    path: "/website-content/banner",
    name: "website-content-banner",
    meta: {
      title: "Banner",
      authRequired: true,
    },
    component: () => import("../views/website-content/banner/index.vue"),
  },
  {
    path: "/website-content/service-catalog",
    name: "website-content-service-catalog",
    meta: {
      title: "Katalog Layanan",
      authRequired: true,
    },
    component: () => import("../views/website-content/service-catalog/index.vue"),
  },
  {
    path: "/website-content/service-catalog/create",
    name: "website-content-service-catalog-create",
    meta: {
      title: "Tambah Katalog Layanan",
      authRequired: true,
    },
    component: () => import("../views/website-content/service-catalog/form/index.vue"),
  },
  {
    path: "/website-content/service-catalog/:id",
    name: "website-content-service-catalog-detail",
    meta: {
      title: "Detail Katalog Layanan",
      authRequired: true,
    },
    component: () => import("../views/website-content/service-catalog/detail/index.vue"),
  },
  {
    path: "/website-content/service-catalog/:id/edit",
    name: "website-content-service-catalog-edit",
    meta: {
      title: "Edit Katalog Layanan",
      authRequired: true,
    },
    component: () => import("../views/website-content/service-catalog/form/index.vue"),
  },
  {
    // Tab Kategori FAQ: /website-content/faq?tab=category
    path: "/website-content/faq",
    name: "website-content-faq",
    meta: {
      title: "FAQ",
      authRequired: true,
    },
    component: () => import("../views/website-content/faq/index.vue"),
  },
  {
    path: "/website-content/contact",
    name: "website-content-contact",
    meta: {
      title: "Kontak",
      authRequired: true,
    },
    component: () => import("../views/website-content/contact/index.vue"),
  },
  {
    // Tab: ?tab=vision (Visi) | ?tab=mission (Misi); default Tentang Kami
    path: "/website-content/about-us",
    name: "website-content-about-us",
    meta: {
      title: "Tentang Kami",
      authRequired: true,
    },
    component: () => import("../views/website-content/about-us/index.vue"),
  },
  {
    path: "/website-content/medical-equipment",
    name: "website-content-medical-equipment",
    meta: {
      title: "Katalog Alat Kesehatan",
      authRequired: true,
    },
    component: () => import("../views/website-content/medical-equipment/index.vue"),
  },
  {
    path: "/website-content/medical-equipment/create",
    name: "website-content-medical-equipment-create",
    meta: {
      title: "Tambah Katalog Alat Kesehatan",
      authRequired: true,
    },
    component: () => import("../views/website-content/medical-equipment/form/index.vue"),
  },
  {
    path: "/website-content/medical-equipment/:id",
    name: "website-content-medical-equipment-detail",
    meta: {
      title: "Detail Katalog Alat Kesehatan",
      authRequired: true,
    },
    component: () => import("../views/website-content/medical-equipment/detail/index.vue"),
  },
  {
    path: "/website-content/medical-equipment/:id/edit",
    name: "website-content-medical-equipment-edit",
    meta: {
      title: "Edit Katalog Alat Kesehatan",
      authRequired: true,
    },
    component: () => import("../views/website-content/medical-equipment/form/index.vue"),
  },

  {
    // Tab Kategori Artikel: /website-content/article?tab=category
    path: "/website-content/article",
    name: "website-content-article",
    meta: {
      title: "Artikel",
      authRequired: true,
    },
    component: () => import("../views/website-content/article/index.vue"),
  },
  {
    path: "/website-content/article/create",
    name: "website-content-article-create",
    meta: {
      title: "Tambah Artikel",
      authRequired: true,
    },
    component: () => import("../views/website-content/article/form/index.vue"),
  },
  {
    path: "/website-content/article/:id",
    name: "website-content-article-detail",
    meta: {
      title: "Detail Artikel",
      authRequired: true,
    },
    component: () => import("../views/website-content/article/detail/index.vue"),
  },
  {
    path: "/website-content/article/:id/edit",
    name: "website-content-article-edit",
    meta: {
      title: "Edit Artikel",
      authRequired: true,
    },
    component: () => import("../views/website-content/article/form/index.vue"),
  },

  {
    path: "/website-content/article-comment",
    name: "website-content-article-comment",
    meta: {
      title: "Komentar Artikel",
      authRequired: true,
    },
    component: () => import("../views/website-content/article-comment/index.vue"),
  },
  {
    path: "/website-content/seo",
    name: "website-content-seo",
    meta: {
      title: "SEO",
      authRequired: true,
    },
    component: () => import("../views/website-content/seo/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                PUSAT DOKUMEN                               */
  /* -------------------------------------------------------------------------- */
  {
    // Filter via query: /document-center?category=<document_category_id>&status=valid|revoked
    path: "/documents/document-center",
    name: "document-center",
    meta: {
      title: "Pusat Dokumen",
      authRequired: true,
    },
    component: () => import("../views/document-center/index.vue"),
  },

  // ==============================
  // TODO: Tambahkan route fitur Portal IHC lainnya di sini
  // ==============================
  // routes.js — paling bawah!
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/errors/404.vue"),
  },
];